import type { StationExit } from './fetchStationContent'

/**
 * Data extraction for the About SEO block (TASK-699, Figma 8597:41868).
 * No new database fields: the street comes from the station-content
 * address, the landmark list is derived from the exits' nearby lines.
 */

/** "Marylebone Road, NW1 5LA" → "Marylebone Road". */
export function streetFromAddress(address: string | undefined): string | undefined {
  if (address === undefined) return undefined
  const street = address.split(',')[0].trim()
  return street === '' ? undefined : street
}

/**
 * Unique landmarks in first-seen order from the exits' nearby lines
 * ("Madame Tussauds · Regent's Park" → two entries).
 */
export function landmarksFromExits(exits: readonly StationExit[]): string[] {
  const landmarks: string[] = []
  for (const exit of exits) {
    if (exit.nearby === undefined) continue
    for (const part of exit.nearby.split('·')) {
      const landmark = part.trim()
      if (landmark !== '' && !landmarks.includes(landmark)) landmarks.push(landmark)
    }
  }
  return landmarks
}

/**
 * The landmark list the About paragraph renders.
 *
 * Exit-level `nearby` wins when present — it ties a place to the exit
 * you should use, which is strictly more useful — and the station-level
 * `landmarks` list covers everyone else. Only 47 of 270 stations have
 * exits data, so deriving landmarks from exits alone left the sentence
 * on a single page.
 */
export function stationLandmarks(
  content: {
    readonly landmarks?: readonly string[]
    readonly exits?: readonly StationExit[]
  },
  stationName?: string,
): string[] {
  const fromExits = landmarksFromExits(content.exits ?? [])
  const list = fromExits.length > 0 ? fromExits : [...(content.landmarks ?? [])]
  // A station named after the place it serves (Clapham Common, Holland
  // Park, Wimbledon Park) must not be told it is close to itself.
  return stationName === undefined ? list : list.filter((l) => l !== stationName)
}
